import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import useAppContext from '../../../../context/useAppContext';
import {
    ToastContext,
    WeezHeader,
    Button,
    SplitContainer,
    Message,
    Icon,
    FrameCard,
    InputLabel,
    Input,
    ToggleContainer,
    ToggleLabel,
    Section,
    CounterLabel,
    TooltipHelp,
    TranslateDropdown,
    CurrencyInput,
    Form
} from '@weezevent/nacre';
import { fetcher, useForm } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import { Inventory as InventoryModel, Event } from '../../../../models';
import Chart from '../../../../models/chart';
import LoadingCard from '../../../../components/loadingCard';
import { LANGUAGES } from '../../../../components/translations/translationModal';
import EventsPicker from './EventsPicker';
import css from '../../chart-list.module.css';
import i18n from 'i18next';

function Inventory({ chartId, inventoryId }) {
    const location = useLocation();
    const [{ organization }] = useAppContext();
    const { t } = useTranslation();
    const locale = i18n.language;
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);
    const [querySearch, setQuerySearch] = React.useState('');

    const languageOptions = React.useMemo(() => {
        return organization.languages.map(lang => {
            const formatedValue = lang.split('-');
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: formatedValue.length > 1 ? `${formatedValue[0]}-${formatedValue[1]?.toUpperCase()}` : formatedValue[0]
            };
        });
    }, [organization]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-')));
        return defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions]);

    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const isVirtual = React.useMemo(() => {
        if (!location.state) {
            return false;
        }
        return location.state.type === 'virtual';
    }, [location]);

    const editMode = React.useMemo(() => {
        return Boolean(inventoryId);
    }, [inventoryId]);

    const [chart] = Chart.useApiModel({
        organizationId: organization.id,
        id: chartId
    });

    const [form, setForm, setFormObject] = useForm({
        // whitespace in form name to preserve WeezHeader aspect-ratio
        name: ' ',
        events: [],
        social_distancing_x: 0,
        social_distancing_y: 0,
        social_distancing_z: 0,
        displayOrder: 0,
        virtual: isVirtual ? true : false,
        sub_inventories: [],
        translations: {},
        metadata: {
            partial_closed_hearing: false,
            partial_closed_hearing_amount: 0,
            partial_closed_hearing_total: 0,
            regional_league_supplement: false,
            regional_league_supplement_paid_attendees: 0,
            regional_league_supplement_majoration: 0,
            revenue_sheet_display: null
        }
    });

    const [, , syncInventory] = InventoryModel.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            cache: false,
            id: inventoryId,
            launch: editMode,
            onSuccess: _inventory => {
                const defaultMetaData = {
                    partial_closed_hearing: false,
                    partial_closed_hearing_amount: 0,
                    partial_closed_hearing_total: 0,
                    regional_league_supplement: false,
                    regional_league_supplement_paid_attendees: 0,
                    regional_league_supplement_majoration: 0
                };
                if (chart.type !== 'default') defaultMetaData.revenue_sheet_display = _inventory.virtual ? 'season' : 'match';
                setFormObject(
                    {
                        name: _inventory.name,
                        events: [_inventory.event],
                        social_distancing_x: _inventory.social_distancing_x,
                        social_distancing_y: _inventory.social_distancing_y,
                        social_distancing_z: _inventory.social_distancing_z,
                        displayOrder: _inventory.display_order,
                        virtual: _inventory.virtual,
                        sub_inventories: _inventory.sub_inventories,
                        translations: _inventory.translations['name'] || {},
                        metadata: { ...defaultMetaData, ..._inventory.metadata }
                    },
                    true
                );
            }
        },
        [organization, chartId, inventoryId, setFormObject, editMode, chart]
    );

    const [events, loadingEvents, syncEvents] = Event.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            query: {
                time_status: editMode ? null : 'ongoing, upcoming'
            }
        },
        [organization]
    );

    const [inventories, loadingInventories, syncInventories] = InventoryModel.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            cache: false
        },
        [organization, chartId]
    );

    const handleQuery = React.useCallback(value => {
        setQuerySearch(value);
    });

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/inventories`);
    }, [organization, chartId]);

    const handleReset = React.useCallback(() => {
        syncEvents();
        syncInventories();
        syncInventory();
    }, [syncEvents, syncInventories, syncInventory]);

    const handleHeaderCancel = React.useCallback(() => {
        if (!editMode) {
            return handleBack();
        }
        return handleReset();
    }, [editMode, handleBack, handleReset]);

    const handleDistancing = React.useCallback(
        (e, { value, checked }) => {
            setForm(`social_distancing_${value}`, checked ? 1 : 0);
        },
        [setForm]
    );

    const handleResetSocial = React.useCallback(
        value => {
            if (!value) {
                setFormObject({ ...form, social_distancing_x: 0, social_distancing_y: 0, social_distancing_z: 0 });
            }
        },
        [setFormObject]
    );

    const handleName = React.useCallback(
        value => {
            let trans = form.translations;
            trans[selectedNameLanguage] = value;
            if (selectedNameLanguage !== defaultLanguage) {
                if (!value) {
                    delete trans[selectedNameLanguage];
                }
                setForm('translations', trans);
            } else {
                setFormObject({ ...form, name: value, translations: trans });
            }
        },
        [setFormObject, setForm, form, selectedNameLanguage, defaultLanguage]
    );

    const displayAdvancedSettings = React.useMemo(() => {
        return chart?.type !== 'default';
    }, [chart]);

    const hasTranslations = React.useMemo(() => {
        if (!Object.keys(form.translations)) {
            return false;
        }
        return defaultLanguage in form.translations && Object.keys(form.translations).length > 1;
    }, [form, form.translations, defaultLanguage]);

    const payloadItems = React.useMemo(() => {
        return form.events.map(ev => {
            const metadata = form.metadata;
            // handle case where toggle is off --> set values to 0
            if ('partial_closed_hearing' in form.metadata && !metadata.partial_closed_hearing) {
                metadata.partial_closed_hearing_amount = 0;
                metadata.partial_closed_hearing_total = 0;
            }

            if ('regional_league_supplement' in form.metadata && !metadata.regional_league_supplement) {
                metadata.regional_league_supplement_paid_attendees = 0;
                metadata.regional_league_supplement_majoration = 0;
            }

            const light_metadata = { revenue_sheet_display: metadata.revenue_sheet_display };
            return {
                event: ev,
                name: form.events.length > 1 ? events.find(_ev => _ev.id === ev)?.name : form.name,
                social_distancing_x: form.social_distancing_x,
                social_distancing_y: form.social_distancing_y,
                social_distancing_z: form.social_distancing_z,
                display_order: form.displayOrder,
                virtual: form.virtual,
                sub_inventories: form.sub_inventories,
                translations: hasTranslations ? { name: form.translations } : {},
                // full metadata for real inventory & only add revenue_sheet_display in metadata for LFP charts
                metadata: !isVirtual ? metadata : displayAdvancedSettings ? light_metadata : {}
            };
        });
    }, [form, form.events, events, hasTranslations, isVirtual, displayAdvancedSettings]);

    const disableSubmit = React.useMemo(() => {
        return (
            !form.events.length ||
            (form.events.length === 1 && (!form.name || form.name === ' ')) ||
            (!form.metadata?.revenue_sheet_display?.length && displayAdvancedSettings && isVirtual)
        );
    }, [form, displayAdvancedSettings]);

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        if (editMode) {
            payloadItems.forEach(item => {
                fetcher
                    .patch(`/ticket/organizations/${organization.id}/charts/${chartId}/inventories/${inventoryId}`, {
                        data: item
                    })
                    .then(() => {
                        toast.success(t(`ticket.charts.inventory.editSuccess`));
                    })
                    .catch(e => {
                        console.log(e);
                        toast.error(t(`ticket.charts.inventory.editFailure`));
                    })
                    .finally(() => {
                        setSubmitting(false);
                        handleBack();
                    });
            });
        } else {
            payloadItems.forEach(item => {
                fetcher
                    .post(`/ticket/organizations/${organization.id}/charts/${chartId}/inventories`, {
                        data: item
                    })
                    .then(() => {
                        toast.success(t(`ticket.charts.inventory.createSuccess`));
                    })
                    .catch(e => {
                        console.log(e);
                        toast.error(t(`ticket.charts.inventory.createFailure`));
                    })
                    .finally(() => {
                        setSubmitting(false);
                        handleBack();
                    });
            });
        }
    }, [fetcher, organization, chartId, inventoryId, form, setFormObject, handleBack, payloadItems, editMode]);

    const onChangeEvents = React.useCallback(
        selected => {
            let values = { events: selected };
            if (!form.events.length) {
                values['name'] = events.find(ev => ev.id === selected[0]).name;
            }
            setFormObject(values, true);
        },
        [events, form]
    );

    const onChangeInventories = React.useCallback(
        selected => {
            setForm('sub_inventories', selected);
        },
        [setForm]
    );

    const socialItems = React.useMemo(() => {
        return [
            { key: 'x', value: form.social_distancing_x },
            { key: 'y', value: form.social_distancing_y },
            { key: 'z', value: form.social_distancing_z }
        ];
    }, [form]);

    const revenueSheetDisplays = [
        { value: 'match', text: t('ticket.charts.inventory.advanced_setting.revenue_sheet_display_match') },
        { value: 'season', text: t('ticket.charts.inventory.advanced_setting.revenue_sheet_display_season') }
    ];

    const hasSocialDistancing = React.useMemo(() => {
        if (!editMode) {
            return false;
        }
        return form.social_distancing_x !== 0 || form.social_distancing_y !== 0 || form.social_distancing_z !== 0;
    }, [editMode, form]);

    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                isFixed
                rightComponent={[
                    <Button
                        key="Delete"
                        className={css['category-reset-btn']}
                        onClick={handleHeaderCancel}
                        label={
                            editMode ? (
                                <>
                                    <Icon name="Reset" /> <span>{t('ticket.common.action.reset')}</span>
                                </>
                            ) : (
                                t('ticket.common.action.cancel')
                            )
                        }
                    />,
                    <Button
                        key="Display"
                        className={css['category-save-btn']}
                        label={
                            <>
                                <Icon className={css['category-save-mobile-icon']} name="save" size="regular" />
                                <span className={css['category-save-mobile-label']}>{t('ticket.common.action.save')}</span>
                            </>
                        }
                        disabled={disableSubmit}
                        onClick={handleSubmit}
                        primary
                    />
                ]}
                title={editMode ? form.name : t('ticket.charts.chart.inventories_add')}
            />

            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <p>{t('ticket.charts.newInventory.tips')}</p>
                    </Message>
                }
            >
                <FrameCard title={t('ticket.common.informations')}>
                    <LoadingCard loading={submitting} height="115px">
                        <EventsPicker
                            edition={editMode}
                            chartId={chartId}
                            form={form}
                            events={events?.filter(ev => !ev.chart || ev.chart === chartId)}
                            inventories={inventories?.filter(iv => iv.active && !iv.virtual)}
                            onChangeEvents={onChangeEvents}
                            loadingEvents={loadingEvents}
                            loadingInventories={loadingInventories}
                            onChangeInventories={onChangeInventories}
                            handleSearch={handleQuery}
                            virtual={isVirtual}
                            query={querySearch}
                            useInventories
                            eventsDisplay
                            inventoriesDisplay
                        />
                    </LoadingCard>
                </FrameCard>

                <FrameCard title={t('main_menu_customize')}>
                    <LoadingCard loading={submitting} height="115px">
                        <>
                            <div className={css['inventory-language-label']}>
                                <InputLabel label={t('ticket.charts.inventory.inventoryName')} />
                                <TranslateDropdown
                                    defaultValue={defaultLanguage}
                                    languages={languageOptions}
                                    onChange={value => setSelectedNameLanguage(value)}
                                    selected={Object.keys(form.translations)}
                                />
                            </div>
                            <Input
                                value={
                                    !Object.keys(form.translations).length || selectedNameLanguage === defaultLanguage ? form.name : form.translations[selectedNameLanguage] || ''
                                }
                                onChange={({ target: { value } }) => handleName(value)}
                                disabled={form.events.length > 1}
                            />
                        </>

                        <div className={css['display-order-block']}>
                            <CounterLabel
                                label={
                                    <span>
                                        {t('ticket.charts.newInventory.display_order')} <TooltipHelp title={t('ticket.charts.newInventory.display_order_tooltip')} />
                                    </span>
                                }
                                numberDecimals={2}
                                min={0}
                                onChange={(e, value) => setForm('displayOrder', value)}
                                step={1}
                                value={form.displayOrder}
                            />
                        </div>

                        <div className={css['social-block']}>
                            <ToggleContainer
                                title={
                                    <span>
                                        {t('ticket.charts.newInventory.social_distancing.label')}
                                        <TooltipHelp title={t(`ticket.charts.newInventory.social_distancing.subtitle`)} />
                                    </span>
                                }
                                disabled={isVirtual}
                                toggle={hasSocialDistancing}
                                onToggle={handleResetSocial}
                            >
                                <Section>
                                    <div className={css['social-items']}>
                                        {socialItems.map(item => {
                                            return (
                                                <ToggleLabel
                                                    key={item.id}
                                                    inlinePosition="left"
                                                    checked={item.value}
                                                    value={item.key}
                                                    label={t(`ticket.charts.newInventory.social_distancing.social_distancing_${item.key}`)}
                                                    onChange={handleDistancing}
                                                />
                                            );
                                        })}
                                    </div>
                                </Section>
                            </ToggleContainer>
                        </div>
                        {isVirtual && (
                            <Message title={t('ticket.charts.newInventory.social_distancing.virtual_inventory_header')} type="default">
                                <p>{t('ticket.charts.newInventory.social_distancing.virtual_inventory_body')}</p>
                            </Message>
                        )}
                    </LoadingCard>
                </FrameCard>
                {displayAdvancedSettings && (
                    <FrameCard title={t('ticket.charts.inventory.advanced_setting.title')}>
                        {!isVirtual && (
                            <>
                                <Form.Field>
                                    <Form.Label optionalText={t('ticket.common.labelOptional')}>
                                        {t('ticket.charts.inventory.advanced_setting.season_label')}
                                        <TooltipHelp title={t('ticket.charts.inventory.advanced_setting.season_tooltip')} />
                                    </Form.Label>
                                    <Form.Input
                                        placeholder={t('ticket.charts.inventory.advanced_setting.season_placeholder')}
                                        value={form?.metadata?.season}
                                        onChange={(e, { value }) => {
                                            setForm('metadata', { ...form.metadata, season: value });
                                        }}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <Form.Label optionalText={t('ticket.common.labelOptional')}>
                                        {t('ticket.charts.inventory.advanced_setting.matchweek_label')}
                                        <TooltipHelp title={t('ticket.charts.inventory.advanced_setting.matchweek_tooltip')} />
                                    </Form.Label>
                                    <Form.Input
                                        placeholder={t('ticket.charts.inventory.advanced_setting.matchweek_placeholder')}
                                        value={form?.metadata?.match}
                                        onChange={(e, { value }) => {
                                            setForm('metadata', { ...form.metadata, match: value });
                                        }}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <Form.Label optionalText={t('ticket.common.labelOptional')}>
                                        {t('ticket.charts.inventory.advanced_setting.visiting_club_label')}
                                        <TooltipHelp title={t('ticket.charts.inventory.advanced_setting.visiting_club_tooltip')} />
                                    </Form.Label>
                                    <Form.Input
                                        placeholder={t('ticket.charts.inventory.advanced_setting.visiting_club_placeholder')}
                                        value={form?.metadata?.visiting_club}
                                        onChange={(e, { value }) => {
                                            setForm('metadata', { ...form.metadata, visiting_club: value });
                                        }}
                                    />
                                </Form.Field>
                            </>
                        )}
                        {isVirtual && (
                            <Form.Field>
                                <Form.Label>
                                    {t('ticket.charts.inventory.advanced_setting.revenue_sheet_display_label')}
                                    <TooltipHelp title={t('ticket.charts.inventory.advanced_setting.revenue_sheet_display_tooltip')} />
                                </Form.Label>
                                <Form.Select
                                    placeholder={t(`ticket.charts.inventory.advanced_setting.revenue_sheet_display_${form.virtual ? 'season' : 'match'}`)}
                                    options={revenueSheetDisplays}
                                    value={form?.metadata?.revenue_sheet_display}
                                    onChange={(e, { value }) => {
                                        setForm('metadata', { ...form.metadata, revenue_sheet_display: value });
                                    }}
                                />
                            </Form.Field>
                        )}
                        {!isVirtual && (
                            <>
                                <div className={css['toggle-container']}>
                                    <ToggleContainer
                                        toggle={form?.metadata?.partial_closed_hearing}
                                        title={<span>{t('ticket.charts.inventory.advanced_setting.partial_closure')}</span>}
                                        onToggle={() => {
                                            setForm('metadata', { ...form.metadata, partial_closed_hearing: !form.metadata.partial_closed_hearing });
                                        }}
                                    >
                                        <Section>
                                            <Form.Field>
                                                <Form.Label label={t('ticket.charts.inventory.advanced_setting.partial_closure_number_label')} />
                                                <div className={css['counter-label-container']}>
                                                    <CounterLabel
                                                        onChange={(e, value) => {
                                                            setForm('metadata', { ...form.metadata, partial_closed_hearing_amount: value });
                                                        }}
                                                        value={form?.metadata?.partial_closed_hearing_amount}
                                                        numberDecimals={0}
                                                        min={0}
                                                        step={1}
                                                    />
                                                </div>
                                            </Form.Field>
                                            <Form.Field>
                                                <Form.Label label={t('ticket.charts.inventory.advanced_setting.partial_closure_total')} />
                                                <div className={css['currency-input-container']}>
                                                    <CurrencyInput
                                                        onChange={e => {
                                                            setForm('metadata', { ...form.metadata, partial_closed_hearing_total: e.value });
                                                        }}
                                                        currency={organization.currency}
                                                        value={form?.metadata?.partial_closed_hearing_total || 0}
                                                        numberDecimals={2}
                                                        min={0}
                                                        locale={locale}
                                                    />
                                                </div>
                                            </Form.Field>
                                        </Section>
                                    </ToggleContainer>
                                </div>
                                <div className={css['toggle-container']}>
                                    <ToggleContainer
                                        toggle={form?.metadata?.regional_league_supplement}
                                        title={<span>{t('ticket.charts.inventory.advanced_setting.regional_league_supplement')}</span>}
                                        onToggle={() => {
                                            setForm('metadata', { ...form.metadata, regional_league_supplement: !form.metadata.regional_league_supplement });
                                        }}
                                    >
                                        <Section>
                                            <Form.Field>
                                                <Form.Label label={t('ticket.charts.inventory.advanced_setting.regional_league_supplement_number')} />
                                                <div className={css['counter-label-container']}>
                                                    <CounterLabel
                                                        onChange={(e, value) => {
                                                            setForm('metadata', { ...form.metadata, regional_league_supplement_paid_attendees: value });
                                                        }}
                                                        value={form?.metadata?.regional_league_supplement_paid_attendees}
                                                        numberDecimals={0}
                                                    />
                                                </div>
                                            </Form.Field>

                                            <Form.Field>
                                                <Form.Label label={t('ticket.charts.inventory.advanced_setting.regional_league_supplement_total')} />
                                                <div className={css['currency-input-container']}>
                                                    <CurrencyInput
                                                        currency={organization.currency}
                                                        onChange={e => {
                                                            setForm('metadata', { ...form.metadata, regional_league_supplement_majoration: e.value });
                                                        }}
                                                        value={form?.metadata?.regional_league_supplement_majoration || 0}
                                                        numberDecimals={2}
                                                        min={0}
                                                        locale={locale}
                                                    />
                                                </div>
                                            </Form.Field>
                                        </Section>
                                    </ToggleContainer>
                                </div>
                            </>
                        )}
                    </FrameCard>
                )}
            </SplitContainer>
        </>
    );
}

export default Inventory;
